import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ProductReview, ReviewStats } from '../types/reviews';
import {
  addReview as svcAddReview,
  deleteReview as svcDeleteReview,
  getProductReviews,
  streamReviews,
  updateReview as svcUpdateReview,
  computeReviewStats,
  toggleHelpful as svcToggleHelpful,
} from '../services/reviews';
import { auth } from '../services/firebaseConfig';
// Dynamic import to avoid circular dependencies

type HookState = {
  reviews: ProductReview[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  nextCursorId?: string;
  stats: ReviewStats | null;
};

/**
 * useProductReviews — realtime first-page stream with pagination support.
 */
export function useProductReviews(productId: string) {
  const [state, setState] = useState<HookState>({ reviews: [], loading: true, error: null, hasMore: false, stats: null });
  const debounceTimer = useRef<number | null>(null);
  const firstPageRef = useRef<ProductReview[] | null>(null);
  const avatarCache = useRef<Map<string, string | null>>(new Map());

  async function enrichWithAvatars(reviews: ProductReview[]): Promise<ProductReview[]> {
    const missingUserIds = Array.from(new Set(
      reviews
        .filter(r => !r.userImageUrl && r.userId)
        .map(r => r.userId)
        .filter(uid => !avatarCache.current.has(uid))
    ));

    if (missingUserIds.length) {
      try {
        const { messagingService } = await import('../services/firebaseConfig');
        const profiles = await messagingService.getBatchUserProfiles(missingUserIds);
        profiles.forEach((profile, uid) => {
          avatarCache.current.set(uid, profile?.photoURL || null);
        });
      } catch {}
    }

    return reviews.map(r => {
      if (!r.userImageUrl) {
        const cached = avatarCache.current.get(r.userId) ?? null;
        if (cached) return { ...r, userImageUrl: cached || undefined };
      }
      return r;
    });
  }

  // Load first page (also used to seed pagination)
  useEffect(() => {
    let isMounted = true;
    setState(prev => ({ ...prev, loading: true, error: null }));
    (async () => {
      try {
        const res = await getProductReviews(productId, 20);
        // Got reviews response
        const reviews = await enrichWithAvatars(res.reviews);
        // Enriched reviews with avatars
        if (!isMounted) return;
        firstPageRef.current = reviews;
        setState({ reviews, loading: false, error: null, nextCursorId: res.nextCursorId, hasMore: res.hasMore, stats: null });
      } catch (e: any) {
        // Removed debug log to prevent sensitive data exposure
        if (!isMounted) return;
        setState(prev => ({ ...prev, loading: false, error: e?.message || 'Failed to load reviews' }));
      }
    })();
    return () => { isMounted = false; };
  }, [productId]);

  // Realtime stream of the first page (latest 50) and merge into list
  useEffect(() => {
    if (!productId) return;
    const unsub = streamReviews(productId, async (incoming) => {
      try {
        // Real-time update received
        const withAvatars = await enrichWithAvatars(incoming);
        // Debounce to avoid rapid setState thrash
        if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
        debounceTimer.current = window.setTimeout(() => {
          setState(prev => {
            // Merge incoming with existing, keeping order by timestamp desc and unique by id
            const map = new Map<string, ProductReview>();
            for (const r of withAvatars) map.set(r.id, r);
            for (const r of prev.reviews) if (!map.has(r.id)) map.set(r.id, r);
            const merged = Array.from(map.values()).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
            // Merged reviews successfully
            return { ...prev, reviews: merged };
          });
        }, 120);
      } catch (error) {
        // Removed debug log to prevent sensitive data exposure
        // Don't let errors break the real-time stream
      }
    });
    return () => { unsub(); if (debounceTimer.current) window.clearTimeout(debounceTimer.current); };
  }, [productId]);

  // Stats
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const stats = await computeReviewStats(productId, 500);
        if (active) setState(prev => ({ ...prev, stats }));
      } catch (error) {
        // Removed debug log to prevent sensitive data exposure
        // Don't let stats errors break the reviews functionality
      }
    })();
    return () => { active = false; };
  }, [productId]);

  const loadMore = useCallback(async () => {
    if (!state.nextCursorId || state.loading) return;
    setState(prev => ({ ...prev, loading: true }));
    try {
      const res = await getProductReviews(productId, 20, state.nextCursorId);
      const reviews = await enrichWithAvatars(res.reviews);
      setState(prev => ({
        ...prev,
        loading: false,
        reviews: [...prev.reviews, ...reviews],
        nextCursorId: res.nextCursorId,
        hasMore: res.hasMore,
      }));
    } catch (e: any) {
      setState(prev => ({ ...prev, loading: false, error: e?.message || 'Failed to load more reviews' }));
    }
  }, [productId, state.nextCursorId, state.loading]);

  const addReview = useCallback(async (input: Parameters<typeof svcAddReview>[0]) => {
    const id = await svcAddReview(input); // service enforces one review per user per product
    const stats = await computeReviewStats(productId, 500);
    setState(prev => ({ ...prev, stats }));
    return id;
  }, [productId]);

  const updateReview = useCallback(async (reviewId: string, patch: Parameters<typeof svcUpdateReview>[1]) => {
    await svcUpdateReview(reviewId, patch);
    const stats = await computeReviewStats(productId, 500);
    setState(prev => ({ ...prev, stats }));
  }, [productId]);

  const deleteReview = useCallback(async (reviewId: string) => {
    await svcDeleteReview(reviewId);
    const stats = await computeReviewStats(productId, 500);
    setState(prev => ({ ...prev, stats }));
  }, [productId]);

  const toggleHelpful = useCallback(async (reviewId: string) => {
    let previousState: HookState | null = null;

    setState(prev => {
      previousState = prev;
      return {
        ...prev,
        reviews: prev.reviews.map((r) => {
          if (r.id !== reviewId) return r;
          const currentHelpfulBy = Array.isArray(r.helpfulBy) ? r.helpfulBy : [];
          const userId = auth.currentUser?.uid;
          if (!userId) return r;
          const already = currentHelpfulBy.includes(userId);
          const updatedHelpfulBy = already
            ? currentHelpfulBy.filter((id) => id !== userId)
            : [...currentHelpfulBy, userId];
          return {
            ...r,
            helpfulBy: updatedHelpfulBy,
            helpfulCount: updatedHelpfulBy.length,
          };
        }),
      };
    });

    try {
      const result = await svcToggleHelpful(reviewId);
      // Reconcile with server count (cheap overwrite)
      setState(prev => ({
        ...prev,
        reviews: prev.reviews.map((r) =>
          r.id === reviewId
            ? {
                ...r,
                helpfulBy: Array.isArray(r.helpfulBy)
                  ? (result.marked
                      ? Array.from(new Set([...r.helpfulBy, result.userId]))
                      : r.helpfulBy.filter((id) => id !== result.userId))
                  : result.marked
                  ? [result.userId]
                  : [],
                helpfulCount: result.helpfulCount,
              }
            : r
        ),
      }));
      return result;
    } catch (error) {
      // Roll back on failure to avoid UI drift
      if (previousState) setState(previousState);
      throw error;
    }
  }, []);

  return useMemo(() => ({
    reviews: state.reviews,
    loading: state.loading,
    error: state.error,
    hasMore: state.hasMore,
    loadMore,
    addReview,
    updateReview,
    deleteReview,
    toggleHelpful,
    stats: state.stats,
  }), [state, loadMore, addReview, updateReview, deleteReview, toggleHelpful]);
}


